import React from 'react'
import { ErrorMessage, Field, Form, Formik, FormikActions } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import './ScanPage.scss'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { Typeahead } from 'react-bootstrap-typeahead'
import loadingBox from '../../assets/images/loading_box.gif'

import { API } from '../../utils'
import { AuthContext, AuthContextInterface, HasGroup, UserGroup } from '../../contexts'
import { SetNotification } from '../../contexts/NotificationProvider'
import { NotificationType } from '../../components/Notifications'
import { ITSError } from '../Layout/ITSError'
import {
  BATTERY_CHANGE_ACCESS_GROUPS,
  INVENTORY_CONTROL_ACCESS_GROUPS,
  ORDER_NUMBER_ACCESS_GROUPS,
  ScannerListener,
  SHIP_TO_ACCESS_GROUPS,
} from './ScanPage'
import { Article, ArticlesData } from '../../types/DataTypes'
import { getSpecifiedReasonCodes, REASON_CODES } from './reasonCodes'
import features from '../../config/feature-flags'

const isDiscardOrRepairedEvent = (trace: string) => {
  return ['Discarded', 'Repaired'].includes(trace)
}

export interface ScanFormValues {
  'order-no': string
  'article-no': string
  'trace-event': string
  date: string
  locations: string
  'ship-to': string
  'reason-code': string
  'specified-reason-code': string
}

interface Props {
  rawArticles: Article[]
  handleSubmit: (values: ScanFormValues, actions: FormikActions<ScanFormValues>) => void
  getAllOrders: (value: string) => void
  userLocations: string[]
  getScanListener: ScannerListener
  serials: string[][]
  setNotification: SetNotification
  hasGroup: HasGroup
  handleArticleNumberChange: (value: string) => void
}

interface State {
  articleNo: string
}

class ScanOrderFormWithoutArticles extends React.Component<Props, State> {
  state: State = {
    articleNo: '',
  }

  scanFormInitialValues: ScanFormValues = {
    'order-no': '',
    'article-no': '',
    'trace-event': '',
    date: moment().format('YYYY-MM-DD'),
    locations: '',
    'ship-to': '',
    'reason-code': '',
    'specified-reason-code': '',
  }

  setFormikFieldValue: ((field: string, value: any) => void) | null = null

  private scanArticleNoListener?: (event: KeyboardEvent) => void

  scanFormValidationSchema = (
    userRoleRequiredFields: { 'order-no'?: boolean; 'ship-to'?: boolean } = { 'order-no': false, 'ship-to': false },
  ) => {
    const yupScanFormValidationRules = {
      'order-no': Yup.string(),
      'article-no': Yup.string()
        .required('Article Number is required')
        .test('is-article-number-valid', 'Article Number is not valid', this.validateArticleNo),
      'trace-event': Yup.string().required('Trace Event is required'),
      'reason-code': Yup.string().when('trace-event', {
        is: val => ['Discarded', 'Repaired'].includes(val),
        then: Yup.string().required('Product group is required'),
        otherwise: Yup.string().notRequired(),
      }),
      'specified-reason-code': Yup.string().when('trace-event', {
        is: val => ['Discarded', 'Repaired'].includes(val),
        then: Yup.string().required('Description is required'),
        otherwise: Yup.string().notRequired(),
      }),
      date: Yup.date().required('Trace Date is required'),
      locations: Yup.string().required('Location is required'),
      'ship-to': Yup.string(),
    }

    if (userRoleRequiredFields['order-no']) {
      yupScanFormValidationRules['order-no'] = Yup.string().required('Order Number is required')
    }

    if (userRoleRequiredFields['ship-to']) {
      yupScanFormValidationRules['ship-to'] = Yup.string().required('Ship To is required')
    }

    return Yup.object().shape(yupScanFormValidationRules)
  }

  componentDidMount(): void {
    this.scanArticleNoListener = this.props.getScanListener(this.scanArticleNo)
    document.addEventListener('keypress', this.scanArticleNoListener)
  }

  componentWillUnmount(): void {
    if (this.scanArticleNoListener) {
      document.removeEventListener('keypress', this.scanArticleNoListener)
    }
  }

  render() {
    const {
      handleArticleNumberChange,
      handleSubmit,
      getAllOrders,
      hasGroup,
      userLocations,
      serials,
      rawArticles,
    } = this.props

    return (
      <AuthContext.Consumer>
        {(context: AuthContextInterface) => (
          <div className="panel reference-box">
            <div className="panel-heading">Header</div>
            <div className="panel-body">
              <Formik
                initialValues={this.scanFormInitialValues}
                validationSchema={this.scanFormValidationSchema({
                  'order-no': context.hasGroup(ORDER_NUMBER_ACCESS_GROUPS),
                  'ship-to': context.hasGroup(SHIP_TO_ACCESS_GROUPS),
                })}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                render={({ isValid, isSubmitting, handleBlur, setFieldValue, setFieldTouched, values }) => {
                  const specifiedReasonCodes = getSpecifiedReasonCodes(values['reason-code'])

                  this.setFormikFieldValue = setFieldValue
                  return (
                    <Form>
                      {context.hasGroup(ORDER_NUMBER_ACCESS_GROUPS) && (
                        <>
                          <Field
                            type="text"
                            name="order-no"
                            placeholder="Order No."
                            className="form-control dw-input"
                            onBlur={(e: Event) => {
                              getAllOrders((e.target as HTMLInputElement).value)
                              handleBlur(e)
                            }}
                          />
                          <ErrorMessage
                            name="order-no"
                            render={errorMessage => <ITSError errorMessage={errorMessage} />}
                          />
                        </>
                      )}
                      <Typeahead
                        id="article-no"
                        selected={values['article-no'] ? [values['article-no']] : undefined}
                        multiple={false}
                        onChange={(selected: string[]) => {
                          if (selected[0]) {
                            setFieldValue('article-no', selected[0])
                            handleArticleNumberChange(selected[0])
                          }
                        }}
                        onInputChange={text => {
                          setFieldValue('article-no', text)
                          handleArticleNumberChange(text)
                        }}
                        onBlur={() => {
                          setFieldTouched('article-no', true)
                        }}
                        options={rawArticles.map((item: Article) => item.SKU)}
                        placeholder="Article No."
                        inputProps={{
                          className: `form-control dw-input${rawArticles.length > 0 ? ' options-loaded' : ''}`,
                          name: 'article-no',
                          autoComplete: 'off',
                        }}
                        minLength={5}
                      />
                      <ErrorMessage
                        name="article-no"
                        render={errorMessage => <ITSError errorMessage={errorMessage} />}
                      />
                      <Field
                        component="select"
                        name="trace-event"
                        className="dw-input"
                        onChange={({ target }: { target: HTMLInputElement }) => {
                          setFieldValue('trace-event', target.value)
                          if (!hasGroup([UserGroup.Franchise])) {
                            setFieldValue('reason-code', '')
                            setFieldValue('specified-reason-code', '')
                          }
                        }}
                      >
                        <option value="">Select Trace Type</option>
                        <option value="Inbound">Inbound</option>
                        <option value="Outbound">Outbound</option>
                        {hasGroup(BATTERY_CHANGE_ACCESS_GROUPS) && (
                          <>
                            <option value="Battery change">Battery change</option>
                          </>
                        )}
                        {hasGroup(INVENTORY_CONTROL_ACCESS_GROUPS) && (
                          <>
                            <option value="Inventory control">Inventory control</option>
                          </>
                        )}
                        {!hasGroup([UserGroup.Franchise]) && (
                          <>
                            <option value="Discarded">Discarded</option>
                            {features && features.traces_scanning_warrants && (
                              <option value="Repaired">Repaired</option>
                            )}
                            <option value="Stolen">Stolen</option>
                            <option value="Returned">Returned</option>
                          </>
                        )}
                      </Field>
                      <ErrorMessage
                        name="trace-event"
                        render={errorMessage => <ITSError errorMessage={errorMessage} />}
                      />
                      {features &&
                        features.traces_scanning_warrants &&
                        !hasGroup([UserGroup.Franchise]) &&
                        isDiscardOrRepairedEvent(values['trace-event']) && (
                          <>
                            <Field
                              component="select"
                              name="reason-code"
                              className="dw-input"
                              onChange={({ target }: { target: HTMLInputElement }) => {
                                if (target && target.value) {
                                  setFieldValue('reason-code', target.value)
                                  setFieldValue('specified-reason-code', '')
                                }
                              }}
                            >
                              <option value="">Select product group</option>
                              {REASON_CODES.map(code => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="reason-code"
                              render={errorMessage => <ITSError errorMessage={errorMessage} />}
                            />
                            <Field component="select" name="specified-reason-code" className="dw-input">
                              <option value="">Select description</option>
                              {specifiedReasonCodes.map(code => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="specified-reason-code"
                              render={errorMessage => <ITSError errorMessage={errorMessage} />}
                            />
                          </>
                        )}
                      <Field type="date" name="date" placeholder="Date" className="form-control dw-input" />
                      <ErrorMessage name="date" render={errorMessage => <ITSError errorMessage={errorMessage} />} />
                      {!!userLocations.length && (
                        <Field component="select" name="locations" className="dw-input">
                          <option value="">Select location</option>
                          {userLocations.map((location: string, i: number) => (
                            <option value={location} key={i}>
                              {location}
                            </option>
                          ))}
                        </Field>
                      )}
                      <ErrorMessage
                        name="locations"
                        render={errorMessage => <ITSError errorMessage={errorMessage} />}
                      />
                      {context.hasGroup(SHIP_TO_ACCESS_GROUPS) && (
                        <>
                          <div className="distributor-form">
                            <label>Ship To</label>
                            <Field
                              component="textarea"
                              rows="2"
                              cols="30"
                              name="ship-to"
                              className="form-control dw-input"
                            />
                          </div>
                          <ErrorMessage
                            name="ship-to"
                            render={errorMessage => <ITSError errorMessage={errorMessage} />}
                          />
                        </>
                      )}
                      {!isSubmitting ? (
                        <button
                          type="submit"
                          id="scan-form-submit-btn"
                          className="btn btn-default dw-btn dw-greenbtn"
                          disabled={!isValid || !serials.length}
                        >
                          Send
                        </button>
                      ) : (
                        <img src={loadingBox} className="loading" alt="loading" />
                      )}
                    </Form>
                  )
                }}
              />
            </div>
          </div>
        )}
      </AuthContext.Consumer>
    )
  }

  private validateArticleNo = (articleNo: string = ''): boolean => {
    if (!this.props.rawArticles) {
      this.props.setNotification({
        text: 'Failed to get article number list. Please try again later.',
        type: NotificationType.ERROR,
      })
      return false
    }

    return this.props.rawArticles.map((item: Article) => item.SKU.toUpperCase()).indexOf(articleNo.toUpperCase()) !== -1
  }

  private scanArticleNo = (scannedText: string) => {
    // eg. item;123456789;
    const patternArticleNumber = /^item.*?;([^;\s]*)/i
    // eg. 7351234567890
    const patternEAN = /^(73\d{11})$/

    const articleNumber = scannedText.match(patternArticleNumber)
    if (articleNumber && articleNumber[1]) {
      this.setArticleNo(articleNumber[1])
      return
    }

    // If EAN code is scanned
    const EAN = scannedText.match(patternEAN)
    if (EAN && EAN[1]) {
      this.setArticleNo(EAN[1])
      return
    }
  }

  private setArticleNo = (articleNo: string): void => {
    if (!this.props.rawArticles) {
      this.props.setNotification({
        text: 'Failed to get article number list. Please try again later.',
        type: NotificationType.ERROR,
      })
      return
    }
    const article: Article | undefined = this.props.rawArticles.find((item: Article) => item.GTIN === articleNo)
    if (this.setFormikFieldValue) {
      this.setFormikFieldValue('article-no', article ? article.SKU : '')
    }
  }

  private getArticleNoAutoComplete = (): Promise<ArticlesData> => {
    return API.get('its-items', '/', {}).catch(() => {
      this.props.setNotification({
        text: 'Failed to get article number list. Please try again later.',
        type: NotificationType.ERROR,
      })
    })
  }
}

export const ScanOrderForm = ScanOrderFormWithoutArticles
